// `hookmyapp sandbox` — Commander registration. Wires the per-subcommand
// runX functions and attaches addExamples() to every command so help.test.ts
// continues to pass.

import type { Command } from 'commander';
import { addExamples } from '../../output/help.js';
import { ValidationError } from '../../output/error.js';
import { runSandboxEnv } from './env.js';
import { runSandboxLogs } from './logs.js';
import { runSandboxSend } from './send.js';
import { runSandboxStart } from './start.js';
import { runSandboxStatus } from './status.js';
import { runSandboxStop } from './stop.js';
import {
  runSandboxWebhookClear,
  runSandboxWebhookSet,
  runSandboxWebhookShow,
} from './webhook.js';

/**
 * Validate `--limit` locally into the API's [1,100] integer range. Returns
 * undefined when omitted (runSandboxLogs defaults to 50). Throws ValidationError
 * (exit 2) on non-numeric / out-of-range input so a bad `--limit abc` is
 * rejected client-side instead of sending `limit=NaN` to the API — which 400s
 * and previously crashed the error renderer (Sentry HOOKMYAPP-CLI-J). Mirrors
 * the channels-logs `parseLimit` validator.
 */
function parseLimitArg(raw: string | undefined): number | undefined {
  if (raw === undefined) return undefined;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 100) {
    throw new ValidationError(
      `--limit must be an integer between 1 and 100 (got "${raw}").`,
    );
  }
  return n;
}

export function registerSandboxCommand(program: Command): void {
  const sandbox = program
    .command('sandbox')
    .description('Manage sandbox sessions for local development');

  const sandboxStart = sandbox
    .command('start')
    .description('Bind a sandbox session for local development')
    .argument('[type]', 'Channel type shortcut: "whatsapp" or "instagram" (same as --type=...)')
    .option(
      '--type <whatsapp|instagram>',
      'Channel type (prompts if omitted; required in --json mode)',
    )
    .option('--listen', 'After bind, immediately start the webhook listener')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        positionalType: string | undefined,
        opts: { type?: 'whatsapp' | 'instagram'; listen?: boolean; json?: boolean },
      ) => {
        // Reconcile positional + flag. If both set and they disagree → ValidationError.
        // If only one is set → that wins. Final value passes through runSandboxStart's
        // existing validation (which rejects unknown strings like "messenger" with INVALID_TYPE).
        let resolvedType = opts.type;
        if (positionalType !== undefined) {
          if (opts.type !== undefined && opts.type !== positionalType) {
            throw new ValidationError(
              `Conflicting channel type: positional "${positionalType}" vs --type="${opts.type}". Pick one.`,
              'CONFLICTING_TYPE',
            );
          }
          resolvedType = positionalType as 'whatsapp' | 'instagram';
        }
        await runSandboxStart({
          ...opts,
          type: resolvedType,
          json: !!(opts.json || program.opts().json),
        });
      },
    );
  addExamples(
    sandboxStart,
    `EXAMPLES:
  $ hookmyapp sandbox start
  $ hookmyapp sandbox start whatsapp
  $ hookmyapp sandbox start instagram --listen
  $ hookmyapp sandbox start whatsapp --json
  $ hookmyapp sandbox start --type=instagram        # flag form still works`,
  );

  const sandboxStatus = sandbox
    .command('status')
    .description('Show active sandbox sessions')
    .option('--json', 'Machine-readable output')
    .action(async (opts: { json?: boolean }) => {
      await runSandboxStatus({ ...opts, json: !!(opts.json || program.opts().json) });
    });
  addExamples(
    sandboxStatus,
    `EXAMPLES:
  $ hookmyapp sandbox status
  $ hookmyapp sandbox status --json`,
  );

  const sandboxStop = sandbox
    .command('stop')
    .description('Delete a sandbox session')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('-y, --yes', 'Skip confirmation')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        identifier: string | undefined,
        opts: {
          phone?: string;
          username?: string;
          session?: string;
          yes?: boolean;
          json?: boolean;
        },
      ) => {
        await runSandboxStop({
          ...opts,
          identifierArg: identifier,
          json: !!(opts.json || program.opts().json),
        });
      },
    );
  addExamples(
    sandboxStop,
    `EXAMPLES:
  $ hookmyapp sandbox stop
  $ hookmyapp sandbox stop --phone 15551234567
  $ hookmyapp sandbox stop --username @ordvir
  $ hookmyapp sandbox stop --session ssn_POWomFvq --yes`,
  );

  const sandboxEnv = sandbox
    .command('env')
    .description('Print or write your sandbox .env values')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--write [path]', 'Write to file (default ./.env)')
    .option('--force', 'Overwrite without prompt')
    .option('--json', 'Machine-readable output')
    .action(
      async function (
        this: Command,
        identifier: string | undefined,
        opts: {
          phone?: string;
          username?: string;
          session?: string;
          write?: string | boolean;
          force?: boolean;
          json?: boolean;
        },
      ) {
        await runSandboxEnv(
          {
            ...opts,
            identifierArg: identifier,
            json: !!(opts.json || program.opts().json),
          },
          this,
        );
      },
    );
  addExamples(
    sandboxEnv,
    `EXAMPLES:
  $ hookmyapp sandbox env
  $ hookmyapp sandbox env --phone 15551234567 --write .env
  $ hookmyapp sandbox env --username @ordvir --write
  $ hookmyapp sandbox env --session ssn_POWomFvq --json`,
  );

  const sandboxSend = sandbox
    .command('send')
    .description('Send a test message via the shared sandbox-proxy')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--message <text>', 'Message body')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        identifier: string | undefined,
        opts: {
          phone?: string;
          username?: string;
          session?: string;
          message?: string;
          json?: boolean;
        },
      ) => {
        await runSandboxSend({
          ...opts,
          identifierArg: identifier,
          json: !!(opts.json || program.opts().json),
        });
      },
    );
  addExamples(
    sandboxSend,
    `EXAMPLES:
  $ hookmyapp sandbox send --phone 15551234567 --message "hi"
  $ hookmyapp sandbox send --username @ordvir --message "hello"
  $ hookmyapp sandbox send --session ssn_POWomFvq --message "ack"`,
  );

  const sandboxLogs = sandbox
    .command('logs')
    .description('List recent webhook deliveries for a sandbox session')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--limit <n>', 'Number of deliveries (1-100, default 50)')
    .option('-f, --follow', 'Stream new deliveries as they arrive (Ctrl+C to stop)')
    .option('-v, --verbose', 'Show the full Meta payload and app response')
    .option('--json', 'JSON array of delivery logs ([] when empty; JSONL when --follow)')
    .action(
      async (
        identifier: string | undefined,
        opts: {
          phone?: string;
          username?: string;
          session?: string;
          limit?: string;
          follow?: boolean;
          verbose?: boolean;
          json?: boolean;
        },
      ) => {
        await runSandboxLogs({
          ...opts,
          limit: parseLimitArg(opts.limit),
          identifierArg: identifier,
          json: !!(opts.json || program.opts().json),
        });
      },
    );
  addExamples(
    sandboxLogs,
    `EXAMPLES:
  $ hookmyapp sandbox logs --username @ordvir
  $ hookmyapp sandbox logs --phone 15551234567 --limit 10
  $ hookmyapp sandbox logs --username @ordvir --follow
  $ hookmyapp sandbox logs --username @ordvir --json | jq`,
  );

  const sandboxWebhook = sandbox
    .command('webhook')
    .description('Manage the destination webhook URL for a sandbox session');

  const webhookShow = sandboxWebhook
    .command('show')
    .description('Show the current webhook URL on a sandbox session')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        identifier: string | undefined,
        opts: { phone?: string; username?: string; session?: string; json?: boolean },
      ) => {
        await runSandboxWebhookShow({ ...opts, identifierArg: identifier, json: !!(opts.json || program.opts().json) });
      },
    );
  addExamples(
    webhookShow,
    `EXAMPLES:
  $ hookmyapp sandbox webhook show --phone 15551234567
  $ hookmyapp sandbox webhook show --username @ordvir
  $ hookmyapp sandbox webhook show --session ssn_POWomFvq`,
  );

  const webhookSet = sandboxWebhook
    .command('set')
    .description('Point this sandbox session at a custom webhook URL')
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--url <url>', 'Webhook URL')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        identifier: string | undefined,
        opts: {
          phone?: string;
          username?: string;
          session?: string;
          url?: string;
          json?: boolean;
        },
      ) => {
        await runSandboxWebhookSet({ ...opts, identifierArg: identifier, json: !!(opts.json || program.opts().json) });
      },
    );
  addExamples(
    webhookSet,
    `EXAMPLES:
  $ hookmyapp sandbox webhook set --phone 15551234567 --url https://my.example/hook
  $ hookmyapp sandbox webhook set --username @ordvir --url https://my.example/hook
  $ hookmyapp sandbox webhook set --session ssn_POWomFvq --url https://my.example/hook`,
  );

  const webhookClear = sandboxWebhook
    .command('clear')
    .description(
      'Clear a custom webhook URL on a sandbox session',
    )
    .argument(
      '[identifier]',
      'Positional shape-detected: phone | @username | ssn_XXXXXXXX',
    )
    .option('--phone <phone>', 'Select WhatsApp session by phone')
    .option('--username <handle>', 'Select Instagram session by @handle')
    .option('--session <ssn_X>', 'Select any session by id (ssn_XXXXXXXX)')
    .option('--json', 'Machine-readable output')
    .action(
      async (
        identifier: string | undefined,
        opts: { phone?: string; username?: string; session?: string; json?: boolean },
      ) => {
        await runSandboxWebhookClear({ ...opts, identifierArg: identifier, json: !!(opts.json || program.opts().json) });
      },
    );
  addExamples(
    webhookClear,
    `EXAMPLES:
  $ hookmyapp sandbox webhook clear --phone 15551234567
  $ hookmyapp sandbox webhook clear --username @ordvir`,
  );

  addExamples(
    sandbox,
    `EXAMPLES:
  $ hookmyapp sandbox start --type=instagram
  $ hookmyapp sandbox status
  $ hookmyapp sandbox env --username @ordvir --write
  $ hookmyapp sandbox send --username @ordvir --message "hello"
  $ hookmyapp sandbox logs --username @ordvir`,
  );

  addExamples(
    sandboxWebhook,
    `EXAMPLES:
  $ hookmyapp sandbox webhook show --phone 15551234567
  $ hookmyapp sandbox webhook set --username @ordvir --url https://my.example/hook
  $ hookmyapp sandbox webhook clear --session ssn_POWomFvq`,
  );
}
